import { deriveAnchoredAge } from '../utils/levelCalculations';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
  ComposedChart, Scatter, ZAxis, Rectangle
} from 'recharts';
import { FaChartBar, FaEdit, FaUser, FaList, FaLanguage } from 'react-icons/fa';
import { DIMENSION_STYLES, UnifiedHeader, UnifiedLegend } from '../styles/DimensionStyles';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Usar funciones centralizadas
import { calculateExpectedPercentage, calculatePreviousYearPercentage } from '../utils/levelCalculations';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean;
}

interface DimensionSummaryProps {
  student: Student;
  dimensionId: number;
}

const COLORS = {
  achieved: '#4A90E2',
  expected: '#F5A623',
  previousYear: '#6B7280'
};

const LEVEL_COLORS = {
  expected: 'bg-yellow-50 border-2 border-yellow-400',    // Color amarillo claro para nivel esperado
  previous: 'bg-gray-50 border-2 border-gray-400',        // Color gris claro para nivel del año anterior
  selected: 'bg-blue-500 text-white shadow-md',           // Color azul para nivel seleccionado
  default: 'bg-white hover:bg-blue-50 border border-gray-200', // Color por defecto
  notApplicable: 'bg-gray-50 text-gray-400 italic cursor-pointer',      // Color para "No aplica" no seleccionado
  notApplicableSelected: 'bg-blue-500 text-white shadow-md italic', // Color para "No aplica" seleccionado
  previouslySelected: 'bg-blue-50 border border-blue-300', // Color para nivel previamente seleccionado
};

const CustomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  return <Rectangle x={x} y={y} width={width} height={height} fill={fill} />;
};

const CustomVerticalRectangle = (props: any) => {
  const { cx, cy, fill } = props;
  return (
    <Rectangle
      x={cx - 1}
      y={cy - 25}
      width={4}
      height={40}
      fill={fill}
    />
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow">
        <p className="label">{`${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={`item-${entry.name}-${index}`} style={{ color: entry.fill }}>
            {`${entry.name}: ${entry.value ? entry.value.toFixed(2) : 'N/A'}%`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Definición de la dimensión y habilidades

export const lenguaInglesaDimension: Dimension = {
  id: 10,
  name: "Acercamiento a la lengua inglesa",
  "skills": [
    {
      "id": 1,
      "name": "OA1 Listen to simple rhymes and parts of songs by demonstrating active listening (e.g., through facial expressions).",
      "levels": [
        "OA1 Listen to simple rhymes and parts of songs by demonstrating active listening (e.g., through facial expressions).",
        "OA.1 Listen to and follow simple rhymes and songs.",
        "OA.1. Listen to and follow simple and short rhymes, as well as making mimics and gestures.",
        "OA.1. Listen to short and simple texts to follow a story sequence (first, then, last).",
        "OA.1. Follow stories read aloud, through gestures, mimic repetition and answering questions.",
        "OA.1. Follow stories read aloud with visual support answering simple questions.",
        "OA1. Listen to and follow a story, short dialogue or informative text with visual support answering explicit questions.",
        "OA1. Listen to and follow a story, short conversation or informative text and identify explicit information.",
        "OA1 Identify explicit and implicit information from short dialogues, stories or informative spoken texts, and respond through oral or written tasks."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 2,
      "name": "OA.2 Listen to and follow one step instruction about daily routine, with visual support (e.g., wash your hands, line up).",
      "levels": [
        "OA.2 Listen to and follow one step instruction about daily routine, with visual support (e.g., wash your hands, line up).",
        "OA2. Listen to and follow simple instructions and/or commands related to classroom and daily routine using visuals.",
        "OA2 Listen to and follow simple instructions and/or commands without visual support.",
        "OA.2. Follow and answer to instructions given by the teacher about known topics. (e.g., Draw a ... next to the house, Circle the...).",
        "OA.2. Follow and answer to instructions, dialogs or conversations about known topics (e.g., Let’s make a sandwich! Take the bread and spread jam. What did we put first?)",
        "OA2 Listen to and follow multi-step instructions and respond to short classroom dialogues on familiar topics.",
        "OA2 Follow multi-step instructions and respond to classroom interactions related to different topics.",
        "OA2 Follow instructions and  respond to simple conversations related to academic content and classroom routines.",
        "OA2 Show comprehension of oral instructions and spoken texts by responding with appropriate actions, choices, or oral/written responses."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 3,
      "name": "OA2. Show comprehension of simple questions by answering with explicit information from a simple oral text with the teacher's support.",
      "levels": [
        "No aplica",
        "No aplica",
        "No aplica",
        "OA2. Show comprehension of simple questions by answering with explicit information from a simple oral text with the teacher's support.",
        "OA2. Show comprehension of the questions by answering with explicit information from a simple oral text.",
        "OA2. Show comprehension of the questions by answering with explicit information from an oral text.",
        "OA2 Show comprehension of the questions by answering with specific information from an oral text.",
        "OA2 Show comprehension of questions by asnwering with specific information from an oral text (e.g., Where did the story take place?) with evidence.",
        "OA2 Show comprehension of  explicit and implicit  questions about  an oral text, providing details or  justification (e.g., How did the character feel? Why?)."  
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 4,
      "name": "OA3. Identify at least 3 vocabulary words learned in the unit.",
      "levels": [
        "OA3. Identify at least 3 vocabulary words learned in the unit.",
        "OA3. Identify at least 5 vocabulary words learned in the unit",
        "OA3. Identify at least 7 vocabulary words learned in the unit",
        "OA3. Identify at least 10 vocabulary words learned in the unit",
        "OA3. Identify at least 12 vocabulary words learned in the unit",
        "OA.3. Identify at least 15 vocabulary words learned in the unit.",
        "OA.3. Identify at least 18 vocabulary words learned in the unit.",
        "OA3 identify at least 20 vocabulary words learned in the unit.",
        "OA3.  Identify at least 23 vocabulary words learned in the unit."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 5,
      "name": "OA.4. Relate drawings  associated to daily routine with the teacher's support.",
      "levels": [
        "OA.4. Relate drawings  associated to daily routine with the teacher's support.",
        "OA.4. Relate vocabulary words/drawings about daily routines with the teacher's support.",
        "OA.4.  Relate vocabulary words/drawings to actions involved in daily routines.",
        "OA.4. Relate  to and follow oral instructions an action involved in their daily routine.",
        "OA.4.Follow and perform daily routine actions based on oral instructions or images.",
        "OA.4.Follow and perform daily routine actions based on oral instructions.",
        "No aplica",
        "No aplica",
        "No aplica"
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 6,
      "name": "OA5. Answer to familiar and yes/ no questions by shaking their head or nodding 'yes' with the support of teacher modeling.",
      "levels": [
        "OA5. Answer to familiar and yes/ no questions by shaking their head or nodding 'yes' with the support of teacher modeling.",
        "OA5. Answer to familiar and yes/ no questions with the teacher's support.",
        "OA5. Answer to questions asked by the teacher (What’s this? How many…? Is it…?) with the teacher's support.",
        "OA5. Answer to Wh- questions asked by the teacher (What, when, where) with the teacher's support.",
        "OA5. Answer to Wh- questions asked by the teacher (What, when, where, who, which and how many) with the teacher's support.",
        "OA5. Answer to Wh- questions asked by the teacher (What, who, why, when, where, which and how many) with the teacher's support.",
        "OA5. Answer to Wh- questions asked by the teacher (What, who, when, where, which, how many and why).",
        "OA5. Answer to Wh- questions asked by the teacher (What, who, when, where, which and how many and why) using evidence.",
        "OA5. Answer to Wh- questions asked by the teacher (What, who, when, where, which, how, how often and how many, how much and why) using evidence."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 7,
      "name": "OA5 Respond to familiar words from songs, stories, or routines using gestures, sounds, or single words with teacher support.",
      "levels": [
        "OA5 Respond to familiar words from songs, stories, or routines using gestures, sounds, or single words with teacher support.",
        "OA5. End short sentences said by the teacher, with common vocabulary.",
        "OA5. Complete sentences said by the teacher, with learned vocabulary and high frequency words and expressions.",
        "OA.5. Participate orally, attempting to use high frequency words and the learnt vocabulary with the teacher's support.",
        "OA.5 Participate using high frequency words and learnt vocabulary and expressions with the teacher's support.",
        "OA5. Participate and make comments using words or short phrases learnt in class with the teacher's support.",
        "OA5. Participate and make comments using words and phrases learned in class.",
        "OA5 Participate in discussions related to an oral text, expressing opinions and preferences using learned vocabulary and high-frequency expressions.",
        "OA5  Express opinions, justify preferences, and respond to others’ ideas in short conversations related to an oral text, using learned vocabulary and high-frequency expressions."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 8,
      "name": "OA6. Follow the guided reading by identifying simple and familiar vocabulary.",
      "levels": [
        "OA6. Follow the guided reading by identifying simple and familiar vocabulary.",
        "OA6. Follow the guided reading by relating the vocabulary with their corresponding images.",
        "OA.6. Follow the guided reading and identify characters and learnt vocabulary.",
        "OA.6. Follow  the guided reading by identify characters, learned vocabulary and high frequency words.",
        "OA.6. Follow the guided reading by identify characters, learned vocabulary, high frequency words, main ideas and actions.",
        "OA6. Follow the guided reading, indentifying characters, learned vocabulary , high frequency words, main ideas, actions and explicit information by answering questions.",
        "OA6 Identify main idea, characters, high frequency words, learnt vocabulary, actions in the text and answer simple comprehension questions or create sentences about what was read.",
        "OA6 Identify main idea, characters, high frequency words, learnt vocabulary, actions in the text and answer comprehension questions using evidence from the text or create sentences about what was read.",
        "OA6  Analyze a text by identifying characters, main ideas, actions and key details, and explain relationships between them through written or spoken responses."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 9,
      "name": "OA6 Read out loud automatically at least 10 high frequency words.",
      "levels": [
        "No aplica",
        "No aplica",
        "No aplica",
        "OA6 Read out loud automatically at least 10 high frequency words.",
        "OA6 Read out loud short and simple phrases/sentences and show comprehension by answering questions.",
        "OA6 Read out loud simple sentences and short texts and show comprehension by answering questions.",
        "OA6 Read out loud simple texts and show comprehension by answering questions.",
        "OA6 Read outloud short and simple stories and texts and show comprehension by answering questions using what was read.",
        "OA6 Read out loud short narrative or informational texts and demonstrate comprehension by summarizing main ideas and answering questions using what was read."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 10,
      "name": "OA6. Follow the reading and understand the main idea of a text.",
      "levels": [
        "No aplica",
        "No aplica",
        "No aplica",
        "OA6. Follow the reading and understand the main idea of a text.",
        "OA6. Identify beginning, middle and end of a story.",
        "OA6. Recognize the main idea and chronological sequence of events in a story.",
        "OA6. Recognize the main idea and sequence of events in a story, organizing what was read in graphic organizers, charts, tables or reorganizing a set of events.",
        "OA6 Illustrate cause-effect relationships, sequence of ideas, or problem-solution using graphic organizers.",
        "OA6 Use graphic organizers to support comprehension by analysing key elements from a text  (e.g., events, characters, structure)."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 11,
      "name": "OA7. Draw a character or part of a text that was previously read (events) with teacher's support.",
      "levels": [
        "No aplica",
        "No aplica",
        "No aplica",
        "OA7. Draw a character or part of a text that was previously read (events) with teacher's support.",
        "OA7. Draw a character or part of a text that was previously read (events).",
        "OA7. Illustrate a character or situation from the text using images.",
        "OA7. Illustrate or act out a passage from the text to show comprehension.",
        "OA7. Represent parts of the story clearly and briefly through theatrical performances or short written texts.",
        "OA7. Represent parts of the story clearly and briefly through theatrical performances or short written texts, incorporating learned vocabulary or connecting it with their own experiences or context."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 12,
      "name": "OA 8  Make reading-related gestures with peers and adults, verbalizing simple known vocabulary.",
      "levels": [
        "OA 8  Make reading-related gestures with peers and adults, verbalizing simple known vocabulary.",
        "OA.8. Make actions while listening to a reading, verbalizing known simple vocabulary.",
        "OA.8. Make actions while listening to a reading, verbalizing known simple vocabulary and simple phrases.",
        "OA.8. Make actions and read along, verbalizing known vocabulary, simple phrases and questions.",
        "OA8 Read along with support, using known vocabulary and high-frequency expressions, showing comprehension through gestures or mimics.",
        "OA8 Read short predictable texts or sentences aloud with guidance, using appropriate pronounciation, intonation and fluency.",
        "OA8. Read short texts aloud or sentences using appropriate pronounciation, volume,  intonation and fluency.",
        "OA8 Read aloud short texts aloud using appropriate pronounciation, volume,  pacing, intonation, fluency and pauses.",
        "OA8 Read aloud short texts or dialogues with appropriate fluency, pronounciation, volume, pacing, intonation and pauses to convey meaning and emotion."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 13,
      "name": "OA 8.  Express preferences about a character,  event or favorite part with the teacher's guidance and visual support.",
      "levels": [
        "No aplica",
        "No aplica",
        "No aplica",
        "No aplica",
        "No aplica",
        "OA 8.  Express preferences about a character,  event or favorite part with the teacher's guidance and visual support.",
        "OA 8. Express preference or opinion about a character or event using  simple sentences.",
        "OA 8. Express preference or opinion about a character or event using  simple sentences and support from what was read.",
        "OA 8. Express preference or opinion about a character or event using  simple sentences and support from what was read and making connections with his/her personal experience."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 14,
      "name": "OA.8.Relate isolated words with images.",
      "levels": [
        "No aplica",
        "OA.8.Relate isolated words with images.",
        "OA.8.. Relate isolated words and phrases with repetitive patterns from different rhymes or chants to images or objects.",
        "OA.8. Follow the reading of chants, simple phrases and choose the corresponding image.",
        "OA8 Relate short, familiar written sentences to corresponding images or scenes.",
        "OA8 Identify key information in short texts and match it with the corresponding illustration or visual element.",
        "OA8 Read simple paragraphs and select or create images that represent key ideas, characters, or settings.",
        "OA8 Interpret short texts and associate parts of the text with visual representations.",
        "OA8 Interpret short literary or informational texts by selecting or designing images that represent main ideas, characters, and inferred meanings."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 15,
      "name": "OA 9. Answer closed questions in oral form, such as “What’s this?”, “How many…?” about read texts in shared reading  using one word or mimic.",
      "levels": [
        "No aplica",
        "No aplica",
        "OA 9. Answer closed questions in oral form, such as “What’s this?”, “How many…?” about read texts in shared reading  using one word or mimic.",
        "OA9.Answer simple oral questions about read texts with one word (like yes, no, one, white, desk).",
        "OA.9 Answer simple oral questions about read texts with a vocabulary word or simple phrase related to such as; slow, fast, they (jump), it isn’t a (zebra).",
        "OA9 Answer simple questions about a text, using short sentences or phrases.",
        "OA9 Answer questions about the text using complete sentences or providing supporting information from the text.",
        "OA9 Answer questions, explaining or describing elements from the text, using appropriate vocabulary and supporting reasons (describing  people, objects and its position, places and daily routines).",
        "OA9 Answer questions, explaining or describing elements from the text, using appropriate vocabulary and supporting reasons, making connections between the text and personal experiences."
      ],
      "selectedLevelIndex": null
    },
    {
      "id": 16,
      "name": "OA9. Show comprehension of simple questions by answering with explicit information from a simple written text with the teacher's support.",
      "levels": [
        "No aplica",
        "No aplica",
        "No aplica",
        "OA9. Show comprehension of simple questions by answering with explicit information from a simple written text with the teacher's support.",
        "OA9. Show comprehension of the questions by answering with explicit information from a simple written text.",
        "OA9. Show comprehension of the questions by answering with explicit information from an written text.",
        "OA9. Show comprehension of the questions by answering with specific information from a written text.",
        "OA9. Show comprehension of questions by asnwering with specific information from a written text (e.g., Where did the story take place?) with evidence.",
        "OA9. Show comprehension of  explicit and implicit  questions about  a written text, providing details or  justification (e.g., How did the character feel? Why?)."
      ],
      "selectedLevelIndex": null
        },
            {
              "id": 17,
              "name": "OA 9. Draw and/or color simple representations of objects or characters based on the story being read aloud.",
              "levels": [
                "No aplica",
                "OA 9. Draw and/or color simple representations of objects or characters based on the story being read aloud.",
                "OA 9. Draw images related to the text or story they heard and name the characters or items.",
                "OA.9. Answer simple oral questions (what's this?, how many...?, is it..? What colour is…?) about read texts , with one word,  (like yes, no, bag, desk, white, one).",
                "OA 9.Describe a key event or character from the story with simple phrases.",
                "OA 9. Describe a specific character, scene, or event, using simple phrases or sentences.",
                "OA9 Describe or create a representation of a specific event, character, or detail from the text, using simple sentences.",
                "OA9 Describe or create a representation of a specific event, character, or detail from the text, providing a brief description.",
                "OA9 Describe the setting or a key moment from the story, explaining how it relates to the plot or the main idea."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 18,
              "name": "OA 10. Repeat orally simple chants or rhymes with support from the teacher.",
              "levels": [
                "OA 10. Repeat orally simple chants or rhymes with support from the teacher.",
                "OA 10. Repeat traditional short and simple rhymes containing learned vocabulary related to the unit with support from the teacher.",
                "OA.10. Repeat rhymes with short, simple and repetitive patterns about letters and words that begin with a sound ( 'A is for apple, a is for ant, a is for alligator….').",
                "OA.10. Repeat  and read rhymes and sing songs trying to imitate the teacher's pronunciation.",
                "OA 10. Repeat and read simple poems, rhymes or chants with correct pronunciation and the teacher's support.",
                "OA10. Repeat and read poems, rhymes or chants, focusing on pronunciation, and intonation with teacher's support.",
                "OA10 Read poems, rhymes, or chants independently or with peers, focusing on pronunciation,  rhythm and fluency.",
                "OA10 Read poems or chants with proper pronunciation, focusing on the sounds, pacing  and intonation of the language.",
                "OA10 Read poems or chants fluently, focusing on clear pronunciation, rhythm, and appropriate tone."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 19,
              "name": "OA 10. Repeat and imitate familiar sounds and single words in songs or chants with teacher’s support. (e.g., cat, dog, sun)",
              "levels": [
                "OA 10. Repeat and imitate familiar sounds and single words in songs or chants with teacher’s support. (e.g., cat, dog, sun)",
                "OA 10. Repeat simple words and short phrases with clear, learned pronunciation. (e.g., Hello, bye, my name is…)",
                "OA 10.Repeat and sing along with basic songs, rhymes, or chants using learned vocabulary. (e.g., “Head, Shoulders, Knees and Toes”)",
                "OA.10 Repeat phrases from rhymes and chants with attention to pronunciation of initial sounds. (e.g., /w/ as in water, /th/ as in three)",
                "OA.10 Identify and reproduce learned words focusing on contrastive sounds like /b/ vs. /v/. (e.g., bat vs. vat)",
                "OA.10 Pronounce words intelligibly that include common digraph sounds such as /h/, /j/, /sh/, and /ch/. (e.g., house, juice, ship, cheese).",
                "OA.10 Repeat and pronounce clearly words with more challenging sounds like /th/ (voiceless), /ð/ (voiced), and /ʤ/ (as in “judge”). (e.g., thanks, this, giant).",
                "OA.10 Reproduce short oral texts with accurate pronunciation of key sounds. (e.g., chants, rhymes, or short dialogues).",
                "OA.10 Produce a short oral or written text applying correct pronunciation of key phonemes and intonation patterns from learned vocabulary and phrases."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 20,
              "name": "OA11. Can communicate simple ideas in a logical and comprehensive way.",
              "levels": [
                "No aplica",
                "No aplica",
                "No aplica",
                "No aplica",
                "No aplica",
                "OA11. Can communicate simple ideas in a logical and comprehensive way.",
                "OA11. Can communicate ideas in a logical and comprehensive way and is able to answer simple questions about the topic.",
                "OA11. Can communicate ideas in a logical and comprehensive way and is able to answer questions about the topic.",
                "OA11.Can express their ideas and thoughts in a logical and comprehensive way using a variety of structures, and supporting their point of view."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 21,
              "name": "OA.11. Use greetings and goodbye expressions like 'Hello, Good morning, Good bye',' How are you? - I'm fine. And you?'",
              "levels": [
                "No aplica",
                "OA.11. Use greetings and goodbye expressions like 'Hello, Good morning, Good bye',' How are you? - I'm fine. And you?'",
                "OA11. Express orally by greeting others (Hello, Goodbye, How are you? I'm … (fine, happy), What's your name? My name is…) and using fixed expressions like 'My favorite toy is ______, I like to _______'.",
                "OA.11. Use expressions orally by greeting others, using good manners (please, thank you) and expressing basic needs:  'Can I drink water? can I go to the toilet please? Can I borrow this pencil?'",
                "OA.11. Use greetings expressions and use some simple sentences to communicate with others.",
                "OA.11. Use frequent expressions in English to communicate their needs: Can I ...? Can you help me? How do you say ...?  Can you repeat that...?",
                "OA.11 Use classroom-related expressions in interactions and is able to participate in short and simple dialogues with their teacher.",
                "OA.11 Use frequent expressions and complete phrases to express their needs and interact in the classroom with their classmates and teacher.",
                "OA.11 Use frequent learned expressions and complete phrases to interact whithin the classroom."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 22,
              "name": "OA.11. Answer to questions asked by the teacher, for example, about personal information like name and age.",
              "levels": [
                "No aplica",
                "OA.11. Answer to questions asked by the teacher, for example, about personal information like name and age.",
                "OA.11. Answer to questions asked by the teacher using isolated words (personal information, name, age and preferences).",
                "OA.11.  Answer to questions asked by the teacher related to familiar topics using words learnt in class (animals, school objects, weather, etc).",
                "OA.11. Answer to questions asked by the teacher using a given structure  (I'm.../Today is.../ My favorite../ I live in...)",
                "OA.11  Answer simple questions about personal information and common topics (school, animals, food, dates, weather, etc.) using simple words.",
                "OA.11 Answer simple questions about common topics using complete sentences.",
                "OA.11 Ask  and answer questions about different topics using present and past tenses.",
                "OA11 Formulate and answer questions using present, past, and future tenses to exchange personal and general information."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 23,
              "name": "OA.11. Identify and name actions carried out by the teacher or a classmate. (e.g., She is jumping. He is walking.)",
              "levels": [
                "No aplica",
                "No aplica",
                "OA.11. Identify and name actions carried out by the teacher or a classmate. (e.g., She is jumping. He is walking.)",
                "OA.11 Describe pictures about a topic following a given structure (It is a______/ It has _______/ It can______)",
                "OA11. Describe  pictures and preferences through simple phrases with the teacher's support.",
                "OA11. Describe  pictures,  talk about personal experiences and express  likes or dislikes through simple phrases.",
                "OA11. Describe  pictures, talk about personal experiences and express likes or dislikes through simple phrases giving an argument (e.g., I like to play football because it's fun).",
                "OA.11 Express in simple sentences to say what you think and  exchange opinions using learned structures. (I like pizza because It is delicious but I don't like ....)",
                "OA.11 Participate in short conversations to express ideas, opinions, and arguments clearly, using examples or reasons. (e.g., I prefer playing outdoors because it’s healthy. I believe animals shouldn’t be kept in zoos because they need space.)"
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 24,
              "name": "OA12. Count up to number 5 (objects, fingers, blocks) with the teacher's support.",
              "levels": [
                "OA12. Count up to number 5 (objects, fingers, blocks) with the teacher's support.",
                "OA12. Identify quantity and count up to number 5.",
                "OA12. Count up to number 10",
                "OA12. Count up to number 20.",
                "OA12. Identify dates, birthdays and ordinal numbers (1st-20th) in simple and known contexts.",
                "OA12. Count up to 100. Identify 100s and 1,000s and ordinal numbers.",
                "OA12. Count up to 100 and indentify 1,000s, dates, years and ordinal numbers",
                "OA12. Identify and count numbers up to 1,000, dates, years and ordinal numbers",
                "OA12. Identify and count numbers up to 1,000,000, dates, years and ordinal numbers"
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 25,
              "name": "OA13. Makes purposeful representations on paper and can say what they mean to express key vocabulary.",
              "levels": [
                "OA13. Makes purposeful representations on paper and can say what they mean to express key vocabulary.",
                "OA.13. Make drawings representing known and simple words.",
                "OA.13. Trace words in the context of creative writing, when labeling drawings.",
                "OA.13. Write words in the context of creative writing, supported by drawings.",
                "OA.13. Write phrases in the context of creative writing supported by drawings and prompts.",
                "OA13. Write simple sentences to express ideas using a cloze/frame/prompt and the teacher's support.",
                "OA13. Write simple sentences to express ideas with the teacher's support, using correct spelling, punctuation and vocabulary.",
                "OA13. Write short texts using simple sentences to share ideas with the teacher's support. using a correct structure, vocabulary, capital letter, punctuation and spelling.",
                "OA13 Write short texts using  correct structure, vocabulary, capital letter, punctuation and spelling supported by the teacher."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 26,
              "name": "OA14. Edit and revise sentences according to the teacher's suggestions.",
              "levels": [
                "No aplica",
                "No aplica",
                "No aplica",
                "No aplica",
                "No aplica",
                "OA14. Edit and revise sentences according to the teacher's suggestions.",
                "OA14. Edit and revise sentences according to the editing code and the teacher's suggestions.",
                "OA14. Edit and revise sentences according to the editing code and the teacher's suggestions with classmates' support if necessary.",
                "OA14. Edit and revise sentences according to the editing code and the teacher's suggestions using the  structures learnt in class with classmates' support if necessary."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 27,
              "name": "OA14. Write sentences in an organized way with a legible handwriting and a nice presentation.",
              "levels": [
                "No aplica",
                "No aplica",
                "No aplica",
                "No aplica",
                "No aplica",
                "OA14. Write sentences in an organized way with a legible handwriting and a nice presentation.",
                "OA14. Write sentences in an organized way that is easy to follow with a legible handwriting and a nice presentation.",
                "OA14. Write sentences in an organized way that is easy to follow with a legible handwriting and a nice presentation that enhance the presentation.",
                "OA14. Write sentences in an organized way that is easy to follow with a legible handwriting and a nice presentation that enhance the presentation and catch the audience attention."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 28,
              "name": "OA.14 Write familiar words using beginning letters or invented spelling.",
              "levels": [
                "No aplica",
                "No aplica",
                "OA.14 Write familiar words using beginning letters or invented spelling.",
                "OA.14. Draw or write words to express an idea (e.g., creating a word bank, word web or mini book with familiar vocabulary words: food, animals, family).",
                "OA.14 Complete or write simple sentences using the learnt words to express an idea with the teacher's support.",
                "OA.14  Complete simple sentences to answer questions and express an idea using the learnt vocabulary with the teacher's support.",
                "OA14.  Complete and write sentences to answer questions and express an idea using the learnt vocabulary.",
                "OA14. Complete and write sentences using the learnt vocabulary to answer questions , express ideas and opinions (e.g., “I think recess should be longer because…”).",
                "OA14 Complete and write well-organized sentences that express and support personal opinions or ideas with details, examples, and reasons, sing the learnt structures and vocabulary."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 29,
              "name": "OA.14  Write simple sentences to answer questions and express an idea using the learnt vocabulary with the teacher's support.",
              "levels": [
                "No aplica",
                "No aplica",
                "No aplica",
                "No aplica",
                "No aplica",
                "OA.14  Write simple sentences to answer questions and express an idea using the learnt vocabulary with the teacher's support.",
                "OA14.  Write sentences to answer questions and express an idea using the learnt vocabulary.",
                "OA14. Write sentences using the learnt vocabulary to answer questions , express ideas and opinions (e.g., “I think recess should be longer because…”).",
                "OA14. Write well-organized sentences that express and support personal opinions or ideas with details, examples, and reasons, sing the learnt structures and vocabulary."
              ],
              "selectedLevelIndex": null
            },
            {
              "id": 30,
              "name": "OA15.  Draw or attempt to write a simple sentence using known vocabulary with the teacher's support.",
              "levels": [
                "No aplica",
                "No aplica",
                "No aplica",
                "OA15.  Draw or attempt to write a simple sentence using known vocabulary with the teacher's support.",
                "OA15. Draw or complete a sentence with key words learnt in the unit with the teacher's support.",
                "OA15. Brainstom key words and complete the teacher's frame/prompt given by the teacher to organize their writings.",
                "OA15. Use graphic organizers to organize the writing using key words and phrases.",
                "OA15. Use graphic organizers to organize the writing of a paragraph using simple sentences.",
                "OA15. Arrange ideas according to a logical sequence before writing."
              ],
              "selectedLevelIndex": null
            },
          ]
        };

const LenguaInglesa: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: 'student' | 'skill';
  handleViewModeChange: (mode: 'student' | 'skill') => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel
}) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");
  const [showAllYears, setShowAllYears] = useState(true);
  const [skillsLoaded, setSkillsLoaded] = useState(true); // Ya están cargados desde el fallback
  
  // Estado para rastrear niveles previamente seleccionados
  const [previouslySelectedLevels, setPreviouslySelectedLevels] = useState<{[key: string]: number | null}>();

  // Funciones de utilidad para colores
  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      if (percentage >= 75) return 'bg-green-600 text-white border-green-600';
      if (percentage >= 50) return 'bg-blue-600 text-white border-blue-600';
      if (percentage >= 25) return 'bg-yellow-600 text-white border-yellow-600';
      return 'bg-red-600 text-white border-red-600';
    }
    if (percentage >= 75) return 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200';
    if (percentage >= 50) return 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200';
    if (percentage >= 25) return 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200';
    return 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200';
  };

  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) return 'text-white';
    if (percentage >= 75) return 'text-green-700';
    if (percentage >= 50) return 'text-blue-700';
    if (percentage >= 25) return 'text-yellow-700';
    return 'text-red-700';
  };

  // Función para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = (fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  };

  const getSkillLevel = (student: Student, dimensionId: number, skillId: number): number | null => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.level ?? null;
  };

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === lenguaInglesaDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  useEffect(() => {
    if (viewMode === 'student' && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(new MouseEvent('click') as any, firstStudent.id as any);
      }
    } else if (viewMode === 'skill' && !selectedSkillId) {
      const firstSkill = lenguaInglesaDimension.skills[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [viewMode, students, selectedStudentIds, handleStudentSelect, selectedSkillId]);

  const SkillsChart = useCallback(({ student }: { student: Student }) => {
    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    const data = lenguaInglesaDimension.skills.map(skill => ({
      name: skill.name,
      previousYearLevel: calculateLastYearPercentage(age),
      percentageAchieved: getSkillLevel(student, lenguaInglesaDimension.id, skill.id) === null 
        ? 0 
        : (getSkillLevel(student, lenguaInglesaDimension.id, skill.id)! + 1) * 20,
      expectedLevel: calculateExpectedLevel(age),
    }));

    const maxSkillNameLength = Math.max(...data.map(d => d.name.length));
    const yAxisWidth = Math.min(200, maxSkillNameLength * 8);
    const barSize = 40;
    const minChartHeight = 200;
    const heightPerItem = 60;
    const chartHeight = Math.max(minChartHeight, data.length * heightPerItem);

    const wrapText = (text: string, maxWidth: number) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = getTextWidth(currentLine + " " + word);
        if (width < maxWidth) {
          currentLine += " " + word;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    const getTextWidth = (text: string) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        context.font = '14px Arial';
        return context.measureText(text).width;
      }
      return 0;
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-bold mb-4">Gráfico de {lenguaInglesaDimension.name}</h3>
        <div style={{ width: '100%', height: `${chartHeight}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              layout="vertical"
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis 
                type="number" 
                domain={[0, 110]}
                ticks={[20, 60, 100]}
                tickFormatter={(value) => {
                  const ageMap: { [key: number]: string } = {
                    20: 'Hacia los 4 años',
                    60: 'Hacia los 6 años',
                    100: 'Hacia los 8 años'
                  };
                  return ageMap[value] || '';
                }}
                tick={{ fontSize: 14, fontWeight: 'bold' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={yAxisWidth}
                tick={({ x, y, payload }) => {
                  const lines = wrapText(payload.value, yAxisWidth - 20);
                  return (
                    <g transform={`translate(${x},${y})`}>
                      {lines.map((line, index) => (
                        <text
                          key={`item-${line}-${index}`}
                          x={-10}
                          y={index * 16}
                          dy={-8 * (lines.length - 1)}
                          textAnchor="end"
                          fill="#333"
                          fontSize={14}
                          fontWeight="bold"
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                }}
                axisLine={false}
                tickLine={false}
              />
              <ZAxis range={[200, 200]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="percentageAchieved" 
                fill={COLORS.achieved} 
                name="Nivel habilidad logrado" 
                barSize={barSize}
              />
              <Bar
                dataKey="expectedLevel"
                fill={COLORS.expected}
                name="Nivel esperado"
                barSize={4}
                shape={<CustomBar />}
              />
              <Scatter 
                dataKey="previousYearLevel" 
                fill={COLORS.previousYear} 
                name="Nivel año anterior" 
                shape={<CustomVerticalRectangle />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }, []);

  const DimensionSummary = useCallback(({ student, dimensionId }: DimensionSummaryProps) => {
    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    const percentageAchieved = lenguaInglesaDimension.skills.reduce((sum, skill) => {
      const level = getSkillLevel(student, dimensionId, skill.id);
      // ✅ CORRECCIÓN: Añadir año base para alinear con edad real seleccionada
      return sum + (level === null ? 0 : (level + 1) * 20 + 20);
    }, 0) / lenguaInglesaDimension.skills.length;

    const data = [{
      name: lenguaInglesaDimension.name,
      previousYearLevel: calculateLastYearPercentage(age),
      percentageAchieved,
      expectedLevel: calculateExpectedLevel(age),
    }];

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-bold mb-4">Resumen de {lenguaInglesaDimension.name}</h3>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart
            layout="vertical"
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis 
              type="number" 
              domain={[0, 110]}
              ticks={[20, 60, 100]}
              tickFormatter={(value) => {
                const ageMap: { [key: number]: string } = {
                  20: 'Hacia los 4 años',
                  60: 'Hacia los 6 años',
                  100: 'Hacia los 8 años'
                };
                return ageMap[value] || '';
              }}
              tick={{ fontSize: 14, fontWeight: 'bold' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={150}
              tick={{ fontSize: 14, fontWeight: 'bold' }}
              axisLine={false}
              tickLine={false}
            />
            <ZAxis range={[200, 200]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              dataKey="percentageAchieved" 
              fill={COLORS.achieved} 
              name="Nivel habilidad logrado" 
              barSize={40}
            />
            <Bar
              dataKey="expectedLevel"
              fill={COLORS.expected}
              name="Nivel esperado"
              barSize={4}
              shape={<CustomBar />}
            />
            <Scatter 
              dataKey="previousYearLevel" 
              fill={COLORS.previousYear} 
              name="Nivel año anterior" 
              shape={<CustomVerticalRectangle />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }, []);

  const handleLocalLevelChange = useCallback((studentId: number | string, skillId: number, levelIndex: number | null) => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (student) {
      const currentLevel = getSkillLevel(student, lenguaInglesaDimension.id, skillId);
    
      if (currentLevel !== null && !previouslySelectedLevels?.[`${studentId}-${skillId}`]) {
        setPreviouslySelectedLevels(prev => ({
          ...prev,
          [`${studentId}-${skillId}`]: currentLevel
        }));
      }
      
      // Lógica de toggle: si el nivel ya está seleccionado, deseleccionarlo
      if (currentLevel === levelIndex) {
        handleLevelChange(studentId, lenguaInglesaDimension.id, skillId, null);
        return;
      }
    }

    // Si levelIndex es -1, convertir a null (para compatibilidad con el botón de reiniciar)
    const actualLevelIndex = levelIndex === -1 ? null : levelIndex;
    handleLevelChange(studentId, lenguaInglesaDimension.id, skillId, actualLevelIndex);
  }, [handleLevelChange, students, previouslySelectedLevels]);

  // useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (skillsLoaded && students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};
      
      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === lenguaInglesaDimension.id && skillLevel.level !== null) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });
      
      // Solo actualizar si hay datos que inicializar
      if (Object.keys(initialLevels).length > 0) {
        setPreviouslySelectedLevels(initialLevels);
      }
    }
  }, [students, skillsLoaded]);



  const renderStudentView = useMemo(() => {
    return () => selectedStudentIds.map(studentId => {
      const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
      const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
      if (!student) return null;

     const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

      const expectedLevel = getExpectedLevel(age);

      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        const studentAge = age;
        const minAge = Math.max(4, studentAge - 2);
        const maxAge = Math.min(12, studentAge + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

      return (
        <div key={student.id} className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de habilidades:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                        const progress = calculateFormProgress(
                          lenguaInglesaDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        const progressPercent = progress.percentage;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";
                        
                        return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-gray-500">
                      {(() => {
                        const progress = calculateFormProgress(
                          lenguaInglesaDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                      })()}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                    
                    <button
                      onClick={() => {
                        if (window.confirm("¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante?")) {
                          lenguaInglesaDimension.skills.forEach(skill => {
                            handleLocalLevelChange(student.id as any, skill.id, -1);
                          });
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                    >
                      Reiniciar
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                    <tr>
                      <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">Habilidad</th>
                      {visibleYears.map((ageGroup, index) => {
                        const actualIndex = visibleIndices[index];
                        return (
                          <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                            <div className="flex flex-col items-center">
                              <span className="mb-1">{ageGroup}</span>
                              <button
                                onClick={() => {
                                  lenguaInglesaDimension.skills.forEach(skill => {
                                    const currentLevel = getSkillLevel(student, lenguaInglesaDimension.id, skill.id);
                                    if (currentLevel === actualIndex) {
                                      handleLocalLevelChange(student.id as any, skill.id, null);
                                    } else {
                                      handleLocalLevelChange(student.id as any, skill.id, actualIndex);
                                    }
                                  });
                                }}
                                className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                              >
                                Seleccionar todo
                              </button>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                {lenguaInglesaDimension.skills.map((skill: Skill, skillIndex: number) => {
                  const selectedLevel = getSkillLevel(student, lenguaInglesaDimension.id, skill.id);
                  const skillIsNotEvaluated = isSkillNotEvaluated(student, lenguaInglesaDimension.id, skill.id);
                  
                  const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                  return (
                        <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                          <td className="py-3 px-6 text-sm font-medium text-gray-900">
                            <div className="flex items-start">
                              <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                                {skillIndex + 1}
                              </span>
                              <div className="flex-1">
                                {skill.name}
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, lenguaInglesaDimension.id, skill.id, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                            const level = skill.levels[levelIndex];
                            if (!level) return null;
                            
                            const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;

                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';

                            const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                            const isExpected = levelIndex === expectedLevel;
                            const isPrevious = levelIndex === previousYearLevel;

                            // 1. Fondo según selección o estado base
                            if (isNotApplicable) {
                              // Si es "No aplica", verificar si está seleccionado
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.selected;
                            } else if (previouslySelected) {
                              buttonClass += LEVEL_COLORS.previouslySelected;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                            let extraStyles: React.CSSProperties = {
                              boxSizing: 'border-box'
                            };

                            if (isPrevious) {
                              // Aplicar borde gris grueso para nivel año anterior
                              extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                              }
                            }

                            if (isExpected) {
                              // Si es esperado, aplicar borde amarillo
                              if (!isPrevious) {
                                extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                                if (selectedLevel !== levelIndex) {
                                  extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                                }
                              }
                            }

                            return (
                              <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                                <button
                                  onClick={() => handleLocalLevelChange(student.id as any, skill.id, levelIndex)}
                                  className={buttonClass}
                                  style={extraStyles}
                                  title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                                >
                                  <span className="text-center leading-tight">{level}</span>
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Controles de navegación rápida */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [selectedStudentIds, students, getExpectedLevel, getPreviousYearLevel, showAllYears, handleLocalLevelChange, previouslySelectedLevels]);

  // Estudiantes filtrados y ordenados
  const filteredStudents = useMemo(() => {
    return students
      .filter(student => selectedCourse ? student.course === selectedCourse : true)
      .filter(student => 
        searchTerm === '' || 
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.course.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        if (sortBy === 'lastName') {
          const lastNameA = getFirstLastName(a.name);
          const lastNameB = getFirstLastName(b.name);
          return lastNameA.localeCompare(lastNameB);
        }
        if (sortBy === 'course') return a.course.localeCompare(b.course);
        return 0;
      });
  }, [students, selectedCourse, searchTerm, sortBy, getFirstLastName]);

  const renderSkillView = useMemo(() => {
    return () => {
      if (!selectedSkillId) {
        return (
          <div className="text-center py-8 text-gray-500">
            Selecciona una habilidad para comenzar
          </div>
        );
      }
      
      const skill = lenguaInglesaDimension.skills.find(s => s.id === selectedSkillId);
      if (!skill) {
        return (
          <div className="text-center py-8 text-gray-500">
            Habilidad no encontrada
          </div>
        );
      }

      // Generar array completo de años (4-12)
      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      // Filtrar años según la configuración 
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        // Para vista por habilidad, mostrar un rango fijo de ±2 años
        // calculado basado en la edad promedio de los estudiantes filtrados
        const averageAge = filteredStudents.length > 0 ? 
          filteredStudents.reduce((sum, student) => {
            const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
            return sum + age;
          }, 0) / filteredStudents.length : 6;
        
        const minAge = Math.max(4, Math.floor(averageAge) - 2);
        const maxAge = Math.min(12, Math.floor(averageAge) + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
        const match = year.match(/\d+/);
        const yearNum = match ? parseInt(match[0]) : 4;
        const calculatedIndex = yearNum - 4;
        // Asegurar que nunca sea NaN y usar fallback con index
        return isNaN(calculatedIndex) ? index : calculatedIndex;
      });

      // Estado masivo de "No evaluada" para esta habilidad
      const allStudentsNotEvaluated =
        filteredStudents.length > 0 &&
        filteredStudents.every((student) =>
          isSkillNotEvaluated(student, lenguaInglesaDimension.id, skill.id)
        );

      const toggleNotEvaluatedForAll = () => {
        if (!handleNotEvaluatedChange) return;
        const nextState = !allStudentsNotEvaluated;
        filteredStudents.forEach((student) => {
          handleNotEvaluatedChange(
            student.id,
            lenguaInglesaDimension.id,
            selectedSkillId,
            nextState
          );
        });
      };

      return (
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gray-50 p-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  {skill.name}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAllYears(!showAllYears)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                      showAllYears
                        ? "bg-blue-500 text-white hover:bg-blue-600"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {showAllYears
                      ? "Filtrar por edad (±2 años)"
                      : "Mostrar todos los años (4-12)"}
                  </button>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          "¿Seguro que desea reiniciar todos los niveles de esta habilidad para todos los estudiantes?"
                        )
                      ) {
                        filteredStudents.forEach((student) => {
                          handleLocalLevelChange(
                            student.id as any,
                            skill.id,
                            -1
                          );
                        });
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Reiniciar todo
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                      <div className="flex flex-col items-start gap-1">
                        <span>Estudiante</span>
                        {handleNotEvaluatedChange && (
                          <button
                            onClick={toggleNotEvaluatedForAll}
                            className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                              allStudentsNotEvaluated
                                ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                : "bg-white text-indigo-900 border-white/50 hover:bg-indigo-50"
                            }`}
                            title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                          >
                            {allStudentsNotEvaluated
                              ? "✓ Habilitar evaluación"
                              : "🚫 No evaluar esta habilidad"}
                          </button>
                        )}
                      </div>
                    </th>
                    {visibleYears.map((ageGroup, index) => {
                      const actualIndex = visibleIndices[index];
                      return (
                        <th
                          key={`item-${ageGroup}-${index}`}
                          className="py-3 px-4 text-center text-xs font-medium uppercase tracking-wider"
                        >
                          <div className="flex flex-col items-center">
                            <span className="mb-1 text-white">
                              {ageGroup}
                            </span>
                            <button
                              onClick={() => {
                                filteredStudents.forEach((student) => {
                                  const currentLevel = getSkillLevel(
                                    student,
                                    lenguaInglesaDimension.id,
                                    selectedSkillId
                                  );
                                  if (currentLevel === actualIndex) {
                                    handleLevelChange(
                                      student.id,
                                      lenguaInglesaDimension.id,
                                      selectedSkillId,
                                      null
                                    );
                                  } else {
                                    handleLevelChange(
                                      student.id,
                                      lenguaInglesaDimension.id,
                                      selectedSkillId,
                                      actualIndex
                                    );
                                  }
                                });
                              }}
                              className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all uppercase tracking-wider"
                            >
                              Seleccionar todo
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => {
                    const age = deriveAnchoredAge({ 
                      course: student.course, 
                      birthDate: student.birthDate, 
                      preferCourse: true 
                    });

                    const expectedLevel = getExpectedLevel(age);
                    
                    const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                    const currentLevel = getSkillLevel(
                      student,
                      lenguaInglesaDimension.id,
                      skill.id
                    );

                    const skillIsNotEvaluated = isSkillNotEvaluated(student, lenguaInglesaDimension.id, skill.id);

                    return (
                      <tr key={student.id} className={`hover:bg-gray-50 ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                        <td className="py-4 px-6 whitespace-nowrap">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {student.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .substring(0, 2)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4 flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {student.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {student.course}
                              </div>
                              {skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-orange-600 font-medium">
                                  🚫 No evaluada
                                </div>
                              )}
                              {/* Botón para marcar como No Evaluada */}
                              {handleNotEvaluatedChange && (
                                <div className="mt-2">
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleNotEvaluatedChange(student.id, lenguaInglesaDimension.id, skill.id, !skillIsNotEvaluated);
                                    }}
                                    className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                      skillIsNotEvaluated
                                        ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                    }`}
                                    title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                  >
                                    {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {visibleIndices.map((levelIndex, mapIndex) => {
                          const levelText = skill.levels[levelIndex] || "No Aplica";
                          const isSelected = currentLevel === levelIndex;
                          const isExpected = levelIndex === expectedLevel;
                          const isPrevious = levelIndex === previousYearLevel;
                          const isNotApplicable = levelText === "No Aplica";

                          let cellClass =
                            "py-4 px-4 text-center cursor-pointer transition-all duration-200 ";

                          if (isNotApplicable) {
                            // Si es "No aplica", verificar si está seleccionado
                            if (isSelected) {
                              cellClass += LEVEL_COLORS.notApplicableSelected;
                            } else {
                              cellClass += LEVEL_COLORS.notApplicable;
                            }
                          } else if (isSelected) {
                            cellClass += LEVEL_COLORS.selected;
                          } else if (isExpected) {
                            cellClass += LEVEL_COLORS.expected;
                          } else if (isPrevious) {
                            cellClass += LEVEL_COLORS.previous;
                          } else {
                            cellClass += LEVEL_COLORS.default;
                          }

                          return (
                            <td
                              key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`}
                              className="py-2 px-2 text-center"
                              style={{
                                position: 'relative',
                                zIndex: 1
                              }}
                            >
                              <div
                                onClick={(e) => {
                                  // Las opciones "No aplica" solo se pueden deseleccionar si están seleccionadas
                                  if (isNotApplicable && !isSelected) {
                                    return;
                                  }

                                  console.log('🔍 Div LenguaInglesa clickeado:', {
                                    levelText,
                                    levelIndex,
                                    isNotApplicable,
                                    studentId: student.id,
                                    skillId: skill.id,
                                    skillLevelsLength: skill.levels.length,
                                    eventType: e.type,
                                    target: e.target,
                                    targetTagName: (e.target as HTMLElement).tagName,
                                    currentTarget: e.currentTarget
                                  });

                                  // Verificar que levelIndex sea válido para esta habilidad
                                  if (levelIndex >= skill.levels.length) {
                                    console.log('❌ levelIndex fuera de rango:', levelIndex, '>=', skill.levels.length);
                                    return;
                                  }

                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleLevelChange(
                                    student.id,
                                    lenguaInglesaDimension.id,
                                    skill.id,
                                    levelIndex
                                  );
                                }}
                                onMouseDown={(e) => {
                                  console.log('🔍 MouseDown en div LenguaInglesa:', {
                                    levelText,
                                    levelIndex,
                                    isNotApplicable,
                                    button: e.button,
                                    target: (e.target as HTMLElement).tagName
                                  });
                                }}
                                className={`${cellClass} cursor-pointer`}
                                style={{
                                  pointerEvents: 'auto',
                                  userSelect: 'none',
                                  width: '100%',
                                  height: '100%',
                                  minHeight: '60px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  zIndex: 2
                                }}
                                title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${levelText}`}
                              >
                                <span className="text-center text-xs">{levelText}</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    };
  }, [selectedSkillId, students, handleLevelChange, selectedCourse, getExpectedLevel, getPreviousYearLevel, handleLocalLevelChange, filteredStudents, showAllYears]);

  const renderSelectionButtons = () => {
    if (viewMode === 'student') {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar estudiante..." 
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
                No se encontraron estudiantes con "{searchTerm}"
              </div>
            ) : (
              filteredStudents.map((student) => {
                // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                const progress = calculateFormProgress(
                  lenguaInglesaDimension.id,
                  student.course,
                  student.skillLevels
                );
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                const percentage = progress.percentage;
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
                
                return (
                <button
                  key={student.id}
                  onClick={(event) => handleStudentSelect(event, student.id as any)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, isSelected)
                    }`}
                  >
                    <div className="font-semibold" title={`${student.name} - ${student.course}`}>
                  {student.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                </button>
                );
              })
            )}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥75% completado,</span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥50% completado,</span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">≥25% completado,</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
              <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Ctrl + clic para seleccionar múltiples estudiantes
              </span>
            </div>
          </div>
        </div>
      );
    }
    
      return (
      <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Habilidad:</h3>
          
          <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
            {/* Barra de búsqueda */}
            <div className="relative w-full md:w-96 mx-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
              </div>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                placeholder="Buscar habilidad..." 
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>
            
            {/* Selector de ordenamiento */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="name">Nombre</option>
                <option value="lastName">Número</option>
                <option value="course">% Completado</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {lenguaInglesaDimension.skills
            .filter(skill => 
              searchTerm === '' || 
              skill.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
              if (sortBy === 'name') return a.name.localeCompare(b.name);
              if (sortBy === 'lastName') return a.id - b.id; // Por número
              if (sortBy === 'course') { // Por % completado
                const aCompleted = filteredStudents.filter(student => 
                  getSkillLevel(student, lenguaInglesaDimension.id, a.id) !== null
                ).length;
                const bCompleted = filteredStudents.filter(student => 
                  getSkillLevel(student, lenguaInglesaDimension.id, b.id) !== null
                ).length;
                const aPercentage = filteredStudents.length > 0 ? (aCompleted / filteredStudents.length) * 100 : 0;
                const bPercentage = filteredStudents.length > 0 ? (bCompleted / filteredStudents.length) * 100 : 0;
                return bPercentage - aPercentage;
              }
              return 0;
            })
            .map((skill) => {
              const completed = filteredStudents.filter(student => 
                getSkillLevel(student, lenguaInglesaDimension.id, skill.id) !== null
              ).length;
              const percentage = filteredStudents.length > 0 ? (completed / filteredStudents.length) * 100 : 0;
              
              return (
              <button
                key={skill.id}
                onClick={() => setSelectedSkillId(skill.id)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                    getCompletionColor(percentage, selectedSkillId === skill.id)
                  }`}
                >
                  <div className="font-semibold" title={skill.name}>
                {skill.name}
                  </div>
                  <div className={`text-xs font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                    {percentage.toFixed(1)}% completado
                  </div>
              </button>
              );
            })}
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center flex-wrap">
              <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥75% completado,</span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥50% completado,</span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">≥25% completado,</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">&lt;25% completado</span>
              </div>
            </div>
            <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
              Tip: Busque y ordene para encontrar habilidades específicas
            </span>
          </div>
          </div>
        </div>
      );
  };

  const renderDataInputView = useMemo(() => {
    return () => (
      <div>
        {viewMode === 'student' ? renderStudentView() : renderSkillView()}
      </div>
    );
  }, [viewMode, renderStudentView, renderSkillView]);



  return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName={lenguaInglesaDimension.name}
        icon={FaLanguage}
        skillsCount={lenguaInglesaDimension.skills.length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
        
      {/* Botones de modo de vista */}
        <div className="mt-3">
          <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
            <div className="grid grid-cols-2 gap-1">
              <button
                onClick={() => handleViewModeChange('student')}
                className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                  viewMode === 'student' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FaUser className="mr-1.5 text-sm" />
                <span className="font-medium">Por Estudiante</span>
            </button>
              
            <button
                onClick={() => handleViewModeChange('skill')}
                className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                  viewMode === 'skill' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FaList className="mr-1.5 text-sm" />
                <span className="font-medium">Por Habilidad</span>
            </button>
            </div>
          </div>
        </div>
      
      {renderSelectionButtons()}
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        {renderDataInputView()}
        
        {/* Leyenda de colores para los niveles de las tablas */}
        {(viewMode === 'student' && selectedStudentIds.length > 0) || (viewMode === 'skill' && selectedSkillId !== null) ? (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🎯</span>
              Leyenda de colores para niveles de desarrollo:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel seleccionado (evaluado)</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-400 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel esperado (edad correspondiente)</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 border-2 border-gray-400 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel del año anterior</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-50 border border-blue-300 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel previamente seleccionado</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border border-gray-200 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Sin seleccionar</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 rounded mr-2 flex items-center justify-center">
                  <span className="text-xs text-gray-400">N/A</span>
                </div>
                <span className="text-xs text-gray-700">No aplica para este nivel</span>
              </div>
            </div>
            
            {/* Footer con botón "Volver arriba" */}
            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-4 py-2 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
                </svg>
                Volver arriba
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const calculatePercentageAchieved = (dimension: { skills: Skill[] }) => {
  const totalLevels = dimension.skills.reduce((sum, skill) => sum + skill.levels.length, 0);
  const achievedLevels = dimension.skills.reduce((sum, skill) => {
    return sum + (skill.selectedLevelIndex === null ? 0 : skill.selectedLevelIndex + 1);
  }, 0);
  return (achievedLevels / totalLevels) * 100;
};

const calculateExpectedLevel = (age: number) => {
  return calculateExpectedPercentage(age);
};

const calculateLastYearPercentage = (age: number) => {
  return calculatePreviousYearPercentage(age);
};

export default LenguaInglesa;